import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import priceListActions from "../../../store/actions/items/prices/price.actions";
import { SkeletonTable } from "../../../components/general/skeleton";
import useDelayedFlag from "../../../hooks/useDelayedFlag";

export const GetPriceListsPageComponent = ({ priceListActions }) => {
  const [priceLists, setPriceLists] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const showSkeleton = useDelayedFlag(isLoading);

  // Fetch price lists when the page loads or pagination changes
  useEffect(() => {
    fetchPriceLists();
  }, [pagination.pageNumber, pagination.pageSize]);

  const fetchPriceLists = async () => {
    setIsLoading(true);
    setError(null);

    const queryParams = {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
    };

    try {
      const response = await priceListActions.getPriceLists(queryParams);
      setPriceLists(response.payload.items);
      setPagination((prev) => ({
        ...prev,
        totalItems: response.payload.totalCount,
        totalPages: response.payload.totalPages,
      }));
    } catch (error) {
      console.error("Error fetching price lists:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPageNumber) => {
    setPagination((prev) => ({ ...prev, pageNumber: newPageNumber }));
  };

  const handlePageSizeChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageNumber: 1, // Reset to page 1 when page size changes
    }));
  };

  const handleDeletePriceList = async (priceListId) => {
    if (!window.confirm("Are you sure you want to delete this price list?")) return;

    try {
      await priceListActions.deletePriceList(priceListId);
      alert("Price list deleted successfully!");
      fetchPriceLists(); // Refresh the price lists
    } catch (error) {
      console.error("Error deleting price list:", error);
      alert("Failed to delete price list.");
    }
  };

  const navigateToEditPriceList = (priceListId) => {
    navigate(`/priceLists/edit/${priceListId}`);
  };

  return (
    <div>
      <h2>Price Lists</h2>

      <div>
        <label>Page Size:</label>
        <select value={pagination.pageSize} onChange={handlePageSizeChange}>
          <option value="1">1</option>
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </select>
      </div>

      {isLoading ? (
        showSkeleton && <SkeletonTable rows={10} cols={2} />
      ) : error ? (
        <p>Error: {error.message || "Something went wrong"}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {priceLists.map((priceList) => (
              <tr key={priceList.id}>
                <td>{priceList.name}</td>
                <td>
                  <button onClick={() => navigateToEditPriceList(priceList.id)}>Edit</button>
                  <button onClick={() => handleDeletePriceList(priceList.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div>
        <button disabled={pagination.pageNumber === 1} onClick={() => handlePageChange(pagination.pageNumber - 1)}>
          Previous
        </button>
        <span>
          Page {pagination.pageNumber} of {pagination.totalPages}
        </span>
        <button
          disabled={pagination.pageNumber === pagination.totalPages}
          onClick={() => handlePageChange(pagination.pageNumber + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    priceListActions: bindActionCreators(priceListActions, dispatch),
  };
};

export default connect(null, mapDispatchToProps)(GetPriceListsPageComponent);
